import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserProfile, Session } from '@/types/profile';

export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  jwt: string | null;
  userEmail: string | null;
  userName: string | null;
  profile: UserProfile | null;
  sessions: Session[];
  loginEmail: string;
  totpEnabled: boolean;
  ftpData?: {
    status?: number;
    password?: string;
    hasPassword?: boolean;
  };
}

const initialState: AuthState = {
  isAuthenticated: false,
  token: null,
  jwt: null,
  userEmail: null,
  userName: null,
  profile: null,
  sessions: [],
  loginEmail: "",
  totpEnabled: false,
  ftpData: undefined,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setUserEmail: (state, action: PayloadAction<string>) => {
      state.userEmail = action.payload;
    },
    setUserName: (state, action: PayloadAction<string>) => {
      state.userName = action.payload;
    },
    setProfile: (state, action: PayloadAction<UserProfile | null>) => {
      state.profile = action.payload;
    },
    setSessions: (state, action: PayloadAction<Session[]>) => {
      state.sessions = action.payload;
    },
    setTotpEnabled: (state, action: PayloadAction<boolean>) => {
      state.totpEnabled = action.payload;
    },
    setFtpData: (state, action: PayloadAction<{ status?: number; password?: string; hasPassword?: boolean } | undefined>) => {
      state.ftpData = action.payload;
    },
    setAuthData: (state, action: PayloadAction<{
      token?: string;
      jwt?: string;
      email?: string;
      name?: string;
      profile?: UserProfile;
      sessions?: Session[];
    }>) => {
      state.isAuthenticated = true;
      if (action.payload.token) state.token = action.payload.token;
      if (action.payload.jwt) state.jwt = action.payload.jwt;
      if (action.payload.email) state.userEmail = action.payload.email;
      if (action.payload.name) state.userName = action.payload.name;
      if (action.payload.profile) state.profile = action.payload.profile;
      if (action.payload.sessions) state.sessions = action.payload.sessions;
    },
    setLoginEmail: (state, action: PayloadAction<string>) => {
      state.loginEmail = action.payload;
    },
    clearAuth: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.jwt = null;
      state.userEmail = null;
      state.userName = null;
      state.profile = null;
      state.sessions = [];
      state.totpEnabled = false;
      state.ftpData = undefined;
    },
  },
});

export const {
  setAuthenticated,
  setToken,
  setUserEmail,
  setUserName,
  setProfile,
  setSessions,
  setTotpEnabled,
  setFtpData,
  setAuthData,
  clearAuth,
  setLoginEmail,
} = authSlice.actions;

export default authSlice.reducer;

